import Phaser from 'phaser';
import ABCyaPhaser from '@abcya/abcya_phaser';
import gameData, { GameShortNames } from '../gameData';
import BackgroundLayers from '../game/BackgroundLayers';
import ActiveObjects from '../game/ActiveObjects';
import SoundAssetKeys from '../SoundAssetKeys';
import HudDisplay from '../ui/HudDisplay';
import Santa from '../game/Santa';
import { playFastTempo, playNormalTempo } from '../SoundManager';
import ABCyaLib from '@abcya/abcya_lib';

export default class Game extends Phaser.Scene {
  static get KEY() { return 'Game'; }

  create() {
    this.cameras.main.setBackgroundColor(0x5ecceb);

    this.score = 0;
    this.bestScore = 0;
    this.jumpStrength = 320;
    this.isInvincable = false;
    this.powerupTimer = 0;
    this.windDownTimer = 0;
    this.worldSpeed = 1;
    this.gameOver = true;
    this.gameWaiting = false;

    this.BEGIN_STATE = 10;
    this.PLAY_STATE = 20;
    this.CRASHED_STATE = 29;
    this.GAME_OVER_STATE = 30;

    this.bestScore = parseInt(ABCyaLib.Storage.getObject(`${gameData.STORAGE_PREFIX}bestScore`) ?? '0');

    this.frontLayer = this.add.container();
    this.bgLayer = new BackgroundLayers(this, this.frontLayer);
    this.add.existing(this.bgLayer);

    this.santa = new Santa(this);
    this.add.existing(this.santa);

    this.jumpHandler = this.add.zone(0, 0, this.scale.width, this.scale.height).setOrigin(0);
    this.jumpHandler.setInteractive();
    this.jumpHandler.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => { this.onClick(); });

    this.activeItems = new ActiveObjects(this);
    this.activeItems.on('HIT_OBSTACLE', (obj, x, y) => { this.hitObstacleCollision(obj, x, y); });
    this.activeItems.on('HIT_POWERUP', () => { this.handlePowerupCollision(); });
    this.activeItems.on('HIT_PRESENT', (obj) => { this.handlePresentCollision(obj); });
    this.add.existing(this.activeItems);

    this.hud = new HudDisplay(this);
    this.add.existing(this.hud);

    this.playState = this.BEGIN_STATE;

    this.readyForBegin();
  }

  readyForBegin() {
    this.hud.activateBegin();

    this.santa.setIntroLimits();
  };

  resetGame() {
    console.log('RESET GAME');
    this.score = 0;

    this.hud.hideGameOver();

    // clean up any presents or pipes
    this.activeItems.cleanObjects();
    this.activeItems.resetGaps();

    this.santa.setIntroLimits();
  };

  startOver() {
    this.hud.activateBegin();

    this.jumpHandler.setInteractive();

    this.playState = this.BEGIN_STATE;

    this.santa.showNose();
  };

  onClick() {
    switch (this.playState) {
      case this.BEGIN_STATE:
        this.playState = this.PLAY_STATE;

        this.hud.deactivateBegin();

        this.santa.setGamePlayLimits();
        this.santa.followers[2].unwave();// !!wow!!
        this.santa.jump(this.jumpStrength);
        this.gameOver = false;
        this.activeItems.resetSpawnTimers();
        this.bgLayer.setGameOver(this.gameOver);
        break;

      case this.PLAY_STATE:
        this.santa.jump(this.jumpStrength);
        break;
      case this.CRASHED_STATE:
        break;

      case this.GAME_OVER_STATE:
        break;
    }
  };

  fakeClick() {
    this.santa.jump(this.jumpStrength);
  };

  handlePresentCollision(present) {
    this.score++;

    this.hud.updateScore(this.score);

    this.bgLayer.makeSparkles(present.x + 10, present.y + 23, 30, this.isInvincable);

    this.santa.collectPresent();

    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._coin8, this);

    if (this.score % 30 === 0) {
      this.activeItems.upGapLevel();
    }
  };

  handlePowerupCollision() {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._speed_boost, this);

    playFastTempo(this);

    this.isInvincable = true;
    this.powerupTimer = 8;
    this.windDownTimer = null;
    this.worldSpeed = 2;

    // update bg
    this.bgLayer.changeSpeed(this.worldSpeed);

    // update active items
    this.activeItems.changeForPowerup(this.worldSpeed);
  };

  hitObstacleCollision(obj, x, y) {
    this.bgLayer.makeCaneChunks(x, y, 40);
    this.bgLayer.makeFallingCane(obj);

    if (this.gameOver === false) this.doGameOver();
  };

  doGameOver() {
    if (this.gameOver) return;
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._crash, this);

    this.playState = this.CRASHED_STATE;
    this.gameOver = true;
    this.jumpHandler.disableInteractive();

    let newBest = false;
    if (this.score > this.bestScore) {
      ABCyaLib.Storage.saveObject(`${gameData.STORAGE_PREFIX}bestScore`, this.bestScore.toString());
      newBest = true;
    }
    this.bestScore = Math.max(this.bestScore, this.score);

    this.santa.hideNose();

    this.bgLayer.setGameOver(this.gameOver);

    this.hud.showGameOver(this.score, this.bestScore, newBest);
  };

  update(time, delta) {
    const dt = delta / 1000;

    if (this.isInvincable) {
      this.powerupTimer -= dt;
      if (this.powerupTimer <= 0) {
        //
        if (this.windDownTimer === null) {
          this.windDownTimer = 4;
          this.activeItems.setBlink(true);
        }
        else {
          if (this.windDownTimer <= 0) {
            this.activeItems.setBlink(false);
            this.isInvincable = false;
            this.activeItems.endPowerup(this.worldSpeed);

            playNormalTempo(this);
          }
          else {
            this.windDownTimer -= dt;
            if (this.worldSpeed > 0) {
              this.worldSpeed -= 0.5 * dt;
              if (this.worldSpeed < 1) {
                this.worldSpeed = 1;
              }
              else {
                this.bgLayer.changeSpeed(this.worldSpeed);
                this.activeItems.windSpeed(this.worldSpeed);
              }
            }
          }
        }
      }
    }
    if (this.playState === this.PLAY_STATE) {
      this.activeItems.tick(dt);
    }

    this.santa.move(dt);
    this.santa.tick(dt);
    this.santa.updatePos();
    if (!this.isInvincable && this.santa.isOutOfBounds()) {
      this.doGameOver();
    }

    this.bgLayer.tick(dt);

    this.activeItems.checkCollision(this.santa.driver, this.santa.followers[2], this.isInvincable);
  };
}